import { TFunction } from 'i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import { IBasePublicPage, IPublicPage } from 'src/interfaces/PublicPage';
import {
  normalizePublicPageRefId,
  resolveFullPathsFromSegments,
} from 'src/helpers/publicPageUtils';

const ALL_LANGS: LanguageKeys[] = [LanguageKeys.en, LanguageKeys.hu, LanguageKeys.ro];

export type PublicPageEditorField =
  | 'key'
  | 'redirectTo'
  | 'menu.icon'
  | `labels.${LanguageKeys}`
  | `paths.${LanguageKeys}`;

export type PublicPageEditorErrors = Partial<Record<PublicPageEditorField, string>>;

export function validatePublicPageEditor(
  form: IBasePublicPage,
  pathSegments: Record<LanguageKeys, string>,
  publicPagesById: Record<string, IPublicPage> | undefined,
  t: TFunction,
): PublicPageEditorErrors {
  const errors: PublicPageEditorErrors = {};
  const required = t('form.yup.genericRequired');
  const allLanguages = t('system.pages.validation.allLanguages');

  if (!form.key?.trim()) {
    errors.key = required;
  }

  if (!form.menu.icon?.trim()) {
    errors['menu.icon'] = required;
  }

  let labelsIncomplete = false;
  for (const lang of ALL_LANGS) {
    if (!form.menu.labels[lang]?.trim()) {
      errors[`labels.${lang}`] = required;
      labelsIncomplete = true;
    }
  }

  const resolvedPaths = resolveFullPathsFromSegments(pathSegments, form, publicPagesById);
  let pathsIncomplete = false;
  for (const lang of ALL_LANGS) {
    if (!resolvedPaths[lang]?.trim()) {
      errors[`paths.${lang}`] = required;
      pathsIncomplete = true;
    }
  }

  if (labelsIncomplete) {
    for (const lang of ALL_LANGS) {
      if (errors[`labels.${lang}`]) {
        errors[`labels.${lang}`] = allLanguages;
      }
    }
  }

  if (pathsIncomplete) {
    for (const lang of ALL_LANGS) {
      if (errors[`paths.${lang}`]) {
        errors[`paths.${lang}`] = allLanguages;
      }
    }
  }

  if (form.kind === 'group' && !normalizePublicPageRefId(form.redirectTo)) {
    errors.redirectTo = required;
  }

  return errors;
}

export function labelFieldError(
  errors: PublicPageEditorErrors,
  activeLang: LanguageKeys,
): string | undefined {
  return errors[`labels.${activeLang}`] ?? ALL_LANGS.map(l => errors[`labels.${l}`]).find(Boolean);
}

export function pathFieldError(
  errors: PublicPageEditorErrors,
  activeLang: LanguageKeys,
): string | undefined {
  return errors[`paths.${activeLang}`] ?? ALL_LANGS.map(l => errors[`paths.${l}`]).find(Boolean);
}
